import React, { useRef } from 'react';

interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  error: { line: number; message: string } | null;
}

const Editor: React.FC<EditorProps> = ({ value, onChange, error }) => {
  const lines = value.split('\n');
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Insert 4 spaces for tab (standard for GLSL)
      const tabString = '    ';
      const newValue = value.substring(0, start) + tabString + value.substring(end);

      onChange(newValue);

      // Set cursor position after the inserted tab (need to wait for React to re-render)
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + tabString.length;
      }, 0);
    }
  };

  return (
    <div className="relative w-full h-full bg-zinc-950/40 backdrop-blur-xl border border-white/10 rounded-lg overflow-hidden flex font-mono text-sm">
      {/* Line Numbers Sidebar */}
      <div 
        ref={lineNumbersRef}
        className="w-12 bg-black/20 text-zinc-600 text-right pr-3 pt-4 select-none border-r border-white/5 overflow-hidden"
      >
        {lines.map((_, i) => (
          <div 
            key={i} 
            className={`h-5 leading-5 ${error?.line === i + 1 ? 'text-red-500 bg-red-500/10' : ''}`}
          >
            {i + 1}
          </div>
        ))}
        {/* Extra space at bottom to match textarea padding */}
        <div className="h-10" />
      </div>
      
      {/* Code Input */}
      <div className="flex-1 relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          className="absolute inset-0 w-full h-full bg-transparent text-zinc-300 p-4 pt-4 resize-none outline-none focus:ring-0 leading-5 whitespace-pre overflow-auto scrollbar-thin"
        />
        
        {/* Error Tooltip */}
        {error && (
          <div className="absolute bottom-4 right-4 max-w-xs bg-red-900/80 backdrop-blur-md text-red-100 p-3 rounded border border-red-500/50 text-xs shadow-2xl animate-in fade-in slide-in-from-bottom-2">
            <p className="font-bold mb-1">Compilation Error (Line {error.line})</p>
            <p className="opacity-80 italic">{error.message}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Editor;